import {Component, ElementRef, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {AuthService} from "../../../auth/auth.service";
import {combineLatest, Observable, Subject} from "rxjs";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {
  UserSubscription,
  UserSubscriptionCategory,
  UserSubscriptionIds,
  UserSubscriptionType
} from "../../../user/user-subscription";
import {clientRoles} from "../../../user/user-roles-actions";
import {UserManagementService} from "../../../user-managment/user-management.service";
import {Workspace} from "../../../user-managment/workspace";
import {Router} from "@angular/router";
import {GlobalEvent} from "../../../shared/services/global-event-dispatcher/global-event";
import {
  GlobalEventDispatcherService
} from "../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {UserService} from "../../../user/user.service";
import {User} from "../../../user/user";
import {UserSubscriptionStatus} from "../../../user/user-subscription-status";
import {TypeFormAnswers} from "../../../add-website/typeFormAnswers";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {Config} from "../../../config/config";
import {CookieService} from "ngx-cookie";
import {DbModalService} from "../../../modules/modal/modal";
import {Utils} from "../../../shared/utils/utils";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {SidebarNavigationService} from "../../../shared/services/navigation/sidebar-navigation.service";
import {OneMonthFreeComponent} from "../../../shared/components/dialogs/one-month-free/one-month-free.component";

@Component({
    selector: 'db-primary-workspace',
    templateUrl: './primary-workspace-popup.component.html',
    styleUrls: ['./primary-workspace-popup.component.scss'],
    standalone: false
})

export class PrimaryWorkspacePopupComponent implements OnInit, OnDestroy {

  @Input() isCollapsed: boolean = false;
  @Input() withInviteBlock: boolean = false;
  public Config = Config;
  public showSwitch: boolean = false;
  public workspaceRoles = clientRoles;
  public user: User;
  public workspace: Workspace;
  public workspaces: { [key: string]: Workspace } = {};
  public allWorkspaces: Workspace[] = [];
  public ownerWorkspaceId: number;
  public currentWorkspaceId: number;
  public isChecked: boolean = false;
  public openedKnowledgeBase: boolean;
  public toggledKnowledgeBase$: Observable<boolean>;
  public userSubscription: UserSubscription;
  public UserSubscriptionCategory: typeof UserSubscriptionCategory = UserSubscriptionCategory;
  public userSubscriptionType = UserSubscriptionType;
  public userSubscriptionIds = UserSubscriptionIds;
  public userSubscriptionStatus = UserSubscriptionStatus;
  public isOwnWorkspaceActive: boolean;
  public userLocalStorage: any;
  public hasUpgrade: boolean;
  public hasAgencyUpgrade: boolean;
  public showWorkspaceTour: boolean = false;
  public showWorkspaceSwitchTour: boolean = false;
  public showActivityLogTour: boolean = false;
  public isMobile: boolean;
  public firstTimePopupTitle: string;
  public firstTimePopupDesc: string;
  public firstTimePopupButton1: string;
  public firstTimePopupButton2?: string;
  public hasCloseButton: boolean;
  public currentWorkspace: Workspace;
  public ownerWorkspace: Workspace;
  public switchingWorkspace: boolean = false;
  public typeFormAnswers = TypeFormAnswers;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private userRoleId: number;
  private redirect: any;
  public expiredSubscr: boolean;
  public dbTesting: boolean;
  public demoUser: boolean;

  protected readonly config = Config;

  constructor(
    private $element: ElementRef,
    private _store: Store<fromStore.DbStoreState>,
    private _userManagementService: UserManagementService,
    private _router: Router,
    private _userService: UserService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _cookieService: CookieService,
    private _modalService: DbModalService,
    private _authService: AuthService,
    private sidebarNavigationService: SidebarNavigationService
  ) {
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 991;
    this.dbTesting = !!this._cookieService.get('db-testing');

    this.toggledKnowledgeBase$ = this._store.select(fromStore.getKnowledgeBaseToggled);
    this.toggledKnowledgeBase$
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((isOpened: any) => {
        this.openedKnowledgeBase = isOpened;
      });
    this._store.select(fromStore.getCurrentWorkspace)
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(currentWorkspace => {
        this.userRoleId = currentWorkspace && currentWorkspace.roleId;
        this.currentWorkspace = currentWorkspace;
      });
    combineLatest(
      this._store.select(fromStore.getUserLoaded),
      this._store.select(fromStore.getUserLocalStorageLoaded)
    )
      .pipe(
        filter(([userLoaded, userLocalStorageLoaded]) => !!userLoaded && !!userLocalStorageLoaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getWorkspaces),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getUserUpgradableSubscription()),
          this._store.select(fromStore.getUserAvailableHostingSubscriptions()),
          this._store.select(fromStore.getWorkspaceIdEntities),
          this._store.select(fromStore.getOwnerWorkspace),
          this._store.select(fromStore.getUserLocalStorage)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, workspaces, userSubscription, upgradableSubscriptions, availableHostingSubscriptions, workspaceIds, ownWorkspace, localStorage]) => {
        workspaces.forEach(workspace => {
          if (workspace && !['invited'].includes(workspace.status)) {
            this.workspaces[workspace.workspaceId] = workspace;
          }
        });

        this.allWorkspaces = [];
        workspaces && workspaces.length && workspaces.forEach(workspace => {
          if (workspace && !['invited'].includes(workspace.status)) {
            workspace.roleId === clientRoles.OWNER ? this.allWorkspaces.unshift(workspace) : this.allWorkspaces.push(workspace);
          }
        });

        this.showSwitch = Object.values(this.workspaces).length > 1;
        this.ownerWorkspaceId = workspaceIds.ownerWorkspaceId;
        this.currentWorkspaceId = workspaceIds.currentWorkspaceId || this.ownerWorkspaceId;
        this.user = user;
        this.userSubscription = userSubscription;
        this.expiredSubscr = this.userSubscription && ([this.userSubscriptionStatus.failed, this.userSubscriptionStatus.expired].includes(this.userSubscription.status) || this.userSubscription.isExpired);
        this.userLocalStorage = localStorage;

        this.hasUpgrade = availableHostingSubscriptions.length > 0 && availableHostingSubscriptions.some((subscription) =>
              subscription.type !== UserSubscriptionCategory.STARTER
          );
        this.hasAgencyUpgrade = !!availableHostingSubscriptions.find((subscription) => subscription.isAgency);
        this.isOwnWorkspaceActive = ownWorkspace && !['invited'].includes(ownWorkspace.status);
        this.ownerWorkspace = ownWorkspace;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.changeSelectedWorkspace();
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }


  public gotIt() {
    this.userLocalStorage['hideWorkspaceInviteTour'] = true;
    this.closeFirstTimePopUp("hideWorkspaceInviteTour");
    this.setLocalStorage();
  }

  public gotItActivityLog() {
    this.userLocalStorage['hideActivityLogTour'] = true;
    this.closeFirstTimeActivityLogPopUp("hideActivityLogTour");
    this.setLocalStorage();
  }

  public popupVisibility(): void {
    this.userLocalStorage['popupVisibility'] = true;
    this.setLocalStorage();
  }

  public setLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
    .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  public sendToAnalytics(action): void {
    const analyticsData = {
      eventCategory: 'Client Management',
      eventAction: action,
      eventLabel: 'Popup Visibility'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public switchWorkspace(workspace): void {

    this.hidePopup();
    this.redirect = true;
    this._store.dispatch(new fromStore.SwitchWorkspace({
      switching: true,
      workspaceName: workspace.workspaceName,
      redirect: true
    }));
    this.switchingWorkspace = true;

    this._userManagementService.switchWorkspace(workspace?.workspaceId)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe(response => {
        if (response.success) {
          // this.changeSelectedWorkspace(workspace.workspaceId);
          if (this.redirect) {
            this._router.navigate(['/websites']);
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
          }
        } else {
          this._store.dispatch(new fromStore.SwitchWorkspace({switching: false, workspaceName: '', redirect: true}));
        }
        setTimeout(() => {
          this.switchingWorkspace = false;
        }, 5000);
      });
  }

  private changeSelectedWorkspace(workspaceId?: number): void {
    this.workspace = workspaceId ? this.workspaces[workspaceId] : this.workspaces[this.currentWorkspaceId];
    if (this.workspace) {
      delete this.workspaces[this.workspace.workspaceId];
    }
  }

  public addOwnWorkspace() {
    if (!localStorage.getItem('dbPreviousWorkspaceId') || !localStorage.getItem('dbPreviousWorkspaceRoleId')) {
      localStorage.setItem('dbPreviousWorkspaceId', localStorage.getItem('dbCurrentWorkspaceId'));
      localStorage.setItem('dbPreviousWorkspaceRoleId', localStorage.getItem('dbCurrentWorkspaceRoleId'));
    }
    const switchToWorkspace = {
      ownerWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceRoleId: 1
    };
    this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));

    if (this.userRoleId !== clientRoles.OWNER) {
      this._userManagementService.switchWorkspace(switchToWorkspace.currentWorkspaceId)
        .pipe(
          filter(response => !!response),
          first()
        )
        .subscribe(response => {
          if (response.success) {
            this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
            this._router.navigate(['/upgrade-plan'], {queryParams: {currentUser: true}});
            this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
          }
        });
    }
  }

  public hidePopup(routerLink?): void {
    this.isChecked = false;
    if (routerLink) {
      if (this.demoUser) {
        this._openCheckoutDialog();
      } else {
        this._router.navigate([routerLink]);
      }
    }
  }

  public showCheckoutDialog(from: 'feature' | 'booster' | 'general' | null = 'feature', invitedClient: boolean = false): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: "Upgrade button click",
      eventLabel: from === 'feature' ? "Speed Booster: Invited Upgrade" : "Speed Booster: Pro Button",
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    if (invitedClient && (!localStorage.getItem('dbPreviousWorkspaceId') || !localStorage.getItem('dbPreviousWorkspaceRoleId')) && this.currentWorkspace?.roleId !== clientRoles.OWNER) {
      localStorage.setItem('dbPreviousWorkspaceId', localStorage.getItem('dbCurrentWorkspaceId'));
      localStorage.setItem('dbPreviousWorkspaceRoleId', localStorage.getItem('dbCurrentWorkspaceRoleId'));
    }
    if (!invitedClient) {
      if (this.userSubscription && this.userSubscription.trialBoosterClient) {
        this._router.navigate(['/upgrade-plan']);
      } else {
        if (this.demoUser) {
          const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
          const options = {
            overflowHidden: false,
          };
          if (!firstTime) {
            options['width'] = 1240;
          } else {
            options['width'] = 460;
            options['height'] = 600;
          }
          const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
          modal.componentInstance.showFirstTime = firstTime;
          modal.componentInstance.isInvited = invitedClient;
        } else {
          const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
          modal.componentInstance.mode = from;
          modal.componentInstance.isInvited = invitedClient;
        }
      }
      return;
    }
    const switchToWorkspace = {
      ownerWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceId: localStorage.getItem('dbOwnWorkspaceId'),
      currentWorkspaceRoleId: 1
    };
    this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
    this._userManagementService.switchWorkspace(switchToWorkspace.currentWorkspaceId)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this._globalEventDispatcher.dispatchEvent(GlobalEvent.workspaceSwitch);
          this._store.dispatch(new fromStore.SwitchWorkspace({switching: true, workspaceName: '', redirect: false}));
        }
      });
  }


  private _openCheckoutDialog() {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: 'Locked button click',
      eventLabel: 'Workspace info'
    };
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    return false;
  }

  public closeFirstTimePopUp(event): void {
    event === 'hideWorkspaceSwitchTour' ? this.showWorkspaceSwitchTour = false : this.showWorkspaceTour = false;
    this.isChecked = false;
  }

  public closeFirstTimeActivityLogPopUp(event): void {
    event === 'hideActivityLogTour' ? this.showActivityLogTour = false : this.showActivityLogTour = false;
    this.isChecked = false;
  }

  public sendFreeUpgradeEvent(): void {
    if (this.userSubscription.type !== UserSubscriptionType.FREE) {
      return;
    }
    let analyticsData = {
      eventCategory: this.userSubscription && this.userSubscription.trialBoosterClient ? 'Dashboard Action' : 'Free Upgrade Offer',
      eventAction: "Upgrade page view",
      eventLabel: this.userSubscription && this.userSubscription.trialBoosterClient ? "Speed Booster: Workspace Tooltip" : "Workspace Tooltip"
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    this.hidePopup();
  }

  public sendUpdateEvent(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Update Subscription Click',
      eventLabel: 'Primary Workspace Popup Update Button'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    this.hidePopup();
  }

  public changeValue($event): void {
    this.isChecked = $event.target.checked;
    this.openedKnowledgeBase && this._store.dispatch(new fromStore.ToggleKnowledgeBase());
  }

  public togglePopup(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.isChecked = !this.isChecked;
    this.sidebarNavigationService.isWorkspacePopupOpen.next(this.isChecked);
  }

  public closePopupOnWrapperClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.isChecked = false;
      this.sidebarNavigationService.isWorkspacePopupOpen.next(this.isChecked);
    }
  }

  @HostListener('document:click', ['$event']) onDocumentClick($event: any) {

    if (!this.isMobile && (this.showWorkspaceTour || this.showWorkspaceSwitchTour || this.showActivityLogTour)) {
      $event.stopImmediatePropagation();
      return;
    }
    if (!this.$element.nativeElement.contains($event.target)) {
      this.isChecked = false;
      this.sidebarNavigationService.isWorkspacePopupOpen.next(this.isChecked);
    }
  }

  public logout(): void {
    if (this._authService.canLogout()) {
      // Handle chat/support widgets
      if (typeof window['zE'] !== 'undefined') {
        window['zE']('messenger', 'logoutUser');
      }
      if (typeof window['Smooch'] !== 'undefined') {
        window['Smooch']?.destroy();
        window['Smooch'] = undefined;
      }

      this._authService.logoutFromProfile(null, this.user.id, this.userSubscription);
    }

    this.hidePopup();
  }
}
